import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star } from "lucide-react";

export default function FormRenderer({ fields = [], answers = {}, onChange, readOnly }) {
  const set = (id, v) => onChange && onChange({ ...answers, [id]: v });

  return (
    <div className="space-y-4" data-testid="form-renderer">
      {fields.map((f) => {
        const val = answers[f.id];
        return (
          <div key={f.id} data-testid="render-field">
            <Label className="text-sm font-medium text-slate-800">
              {f.label}{f.required && <span className="text-orange-500"> *</span>}
            </Label>
            <div className="mt-1.5">
              {f.type === "short_text" && <Input disabled={readOnly} value={val || ""} onChange={(e) => set(f.id, e.target.value)} />}
              {f.type === "number" && <Input type="number" disabled={readOnly} value={val ?? ""} onChange={(e) => set(f.id, e.target.value)} />}
              {f.type === "date" && <Input type="date" disabled={readOnly} value={val || ""} onChange={(e) => set(f.id, e.target.value)} />}
              {f.type === "long_text" && <Textarea disabled={readOnly} className="min-h-[90px]" value={val || ""} onChange={(e) => set(f.id, e.target.value)} />}
              {f.type === "yes_no" && (
                <div className="flex gap-2">
                  {["Yes", "No"].map((opt) => (
                    <button key={opt} type="button" disabled={readOnly} onClick={() => set(f.id, opt)}
                      className={`rounded-lg border px-4 py-1.5 text-sm font-medium ${val === opt ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              )}
              {f.type === "single_choice" && (
                <div className="space-y-1.5">
                  {(f.options || []).map((opt) => (
                    <button key={opt} type="button" disabled={readOnly} onClick={() => set(f.id, opt)}
                      className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm ${val === opt ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                      <span className={`h-3.5 w-3.5 rounded-full border ${val === opt ? "border-orange-500 bg-orange-500" : "border-slate-300"}`} /> {opt}
                    </button>
                  ))}
                </div>
              )}
              {f.type === "multi_choice" && (
                <div className="space-y-1.5">
                  {(f.options || []).map((opt) => {
                    const arr = Array.isArray(val) ? val : [];
                    const on = arr.includes(opt);
                    return (
                      <button key={opt} type="button" disabled={readOnly}
                        onClick={() => set(f.id, on ? arr.filter((x) => x !== opt) : [...arr, opt])}
                        className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm ${on ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                        <span className={`h-3.5 w-3.5 rounded border ${on ? "border-orange-500 bg-orange-500" : "border-slate-300"}`} /> {opt}
                      </button>
                    );
                  })}
                </div>
              )}
              {f.type === "dropdown" && (
                <Select value={val || ""} onValueChange={(v) => set(f.id, v)} disabled={readOnly}>
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{(f.options || []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {f.type === "rating" && (
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" disabled={readOnly} onClick={() => set(f.id, n)}>
                      <Star className={`h-6 w-6 ${Number(val) >= n ? "fill-orange-400 text-orange-400" : "text-slate-300"}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {fields.length === 0 && <p className="text-sm text-slate-400">This template has no questions.</p>}
    </div>
  );
}
